// Dispatches by hostname so image transforms happen at the CDN edge rather
// than via Next's /_next/image endpoint. Drops the sharp runtime dependency
// entirely and pushes CPU cost to the upstream CDN.

type LoaderArgs = {
  src: string;
  width: number;
  quality?: number;
};

const CONTENTFUL_IMAGE_HOST = 'images.ctfassets.net';
const CONTENTFUL_PASSTHROUGH_EXTENSIONS = new Set(['.gif', '.svg']);

export function normalizeImageSrc(src: string): string {
  return src.startsWith('//') ? `https:${src}` : src;
}

function getPathExtension(pathname: string): string {
  const filename = pathname.split('/').pop() ?? '';
  const extensionIndex = filename.lastIndexOf('.');

  return extensionIndex === -1 ? '' : filename.slice(extensionIndex).toLowerCase();
}

export function isContentfulRasterImageUrl(src: string): boolean {
  try {
    const url = new URL(normalizeImageSrc(src));

    return (
      url.hostname === CONTENTFUL_IMAGE_HOST &&
      !CONTENTFUL_PASSTHROUGH_EXTENSIONS.has(getPathExtension(url.pathname))
    );
  } catch {
    return false;
  }
}

export default function imageLoader({ src, width, quality }: LoaderArgs): string {
  const q = quality ?? 75;
  const normalizedSrc = normalizeImageSrc(src);

  if (isContentfulRasterImageUrl(normalizedSrc)) {
    // https://www.contentful.com/developers/docs/references/images-api/
    return withQuery(normalizedSrc, { w: String(width), fm: 'avif', q: String(q) });
  }

  // Phase 2: width-to-preset mapping for the Opendoor images worker. Until the
  // OpendoorImage wrapper lands, pass through so URLs already carrying
  // ?preset=... continue to work.
  return normalizedSrc;
}

function withQuery(src: string, params: Record<string, string>): string {
  const url = new URL(src);
  for (const [k, v] of Object.entries(params)) {
    url.searchParams.set(k, v);
  }
  return url.toString();
}
